import React, { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { KeyboardShortcutsModal } from '../components/KeyboardShortcutsModal';
import GTMTracker from '../components/GTMTracker';
import ProtectedRoute from '../components/ProtectedRoute';
import { getMenuItemAccess } from '../config/navMenuItems';

// Import all pages
import Dashboard from '../pages/Dashboard';
import GettingStarted from '../pages/GettingStarted';
import Help from '../pages/Help';
import TeamRequests from '../pages/TeamRequests';
import PatientRecord from '../pages/actors/patient/PatientRecord';
import TodayQueue from '../pages/clinical/TodayQueue';
import RiskRegister from '../pages/clinical/RiskRegister';
import Toolkit from '../pages/clinical/Toolkit';
import Supervision from '../pages/clinical/Supervision';
import Oversight from '../pages/clinical/Oversight';
import AuditLog from '../pages/clinical/AuditLog';
import AdminQuestionnaires from '../pages/admin/AdminQuestionnaires';
import AdminTranslations from '../pages/admin/AdminTranslations';
import ChatSession from '../pages/chat/ChatSession';
import Progress from '../components/Progress';
import Appointment from '../pages/booking/appointment/Appointment';
import AppointmentNew from '../pages/booking/appointment/AppointmentNew';
import AppointmentEdit from '../pages/booking/appointment/AppointmentEdit';
import Patient from '../pages/actors/patient/Patient';
import PatientNew from '../pages/actors/patient/PatientNew';
import PatientEdit from '../pages/actors/patient/PatientEdit';
import ConsultationHistory from '../pages/booking/consultation/ConsultationHistory';
import ConsultationNew from '../pages/booking/consultation/ConsultationNew';
import ConsultationEdit from '../pages/booking/consultation/ConsultationEdit';
import Doctor from '../pages/actors/doctor/Doctor';
import DoctorNew from '../pages/actors/doctor/DoctorNew';
import DoctorEdit from '../pages/actors/doctor/DoctorEdit';
import Avatar from '../pages/chat/avatar/Avatar';
import AvatarNew from '../pages/chat/avatar/AvatarNew';
import AvatarEdit from '../pages/chat/avatar/AvatarEdit';
import Student from '../pages/actors/student/Student';
import StudentNew from '../pages/actors/student/StudentNew';
import StudentEdit from '../pages/actors/student/StudentEdit';
import Login from '../pages/actors/user/Login';
import ForgotPassword from '../pages/actors/user/ForgotPassword';
import ResetPassword from '../pages/actors/user/ResetPassword';
import Register from '../pages/actors/user/Register';
import User from '../pages/actors/user/User';
import UserEdit from '../pages/actors/user/UserEdit';
import Profile from '../pages/actors/user/Profile';
import Settings from '../pages/Settings';
import SubscriptionLocked from '../pages/SubscriptionLocked';
import CalendarPage from '../pages/Calendar';
import DoctorSettings from '../pages/DoctorSettings';
import CreditHistory from '../pages/actors/user/CreditHistory';
import ConnectionTest from '../pages/test/ConnectionTest';
import RagDebugger from '../pages/RagDebugger';
import UploadSession from '../pages/UploadSession';
import Achievements from '../pages/actors/user/Achievements';
import TherapistDashboard from '../pages/dashboard/TherapistDashboard';
import SupervisorReportees from '../pages/supervisor/SupervisorReportees';
import ReporteeSessions from '../pages/supervisor/ReporteeSessions';
import SupervisorDashboard from '../pages/supervisor/SupervisorDashboard';
import ManageSupervisors from '../pages/actors/supervisor/ManageSupervisors';
import ManualReviewDashboard from '../pages/ManualReviewDashboard';
import ReviewSettings from '../pages/ReviewSettings';
import ConsultationReportEditor from '../pages/booking/consultation/ConsultationReportEditor';
import LongitudinalReport from '../pages/analytics/LongitudinalReport';
import ProgressReport from '../pages/analytics/ProgressReport';
import TermsConditions from '../pages/TermsConditions';
import PrivacyPolicy from '../pages/PrivacyPolicy';
import ErrorLogs from '../pages/ErrorLogs';
import AdminOrganizations from '../pages/admin/AdminOrganizations';
import OrganizationCredits from '../pages/OrganizationCredits';
import AdminConsultations from '../pages/AdminConsultations';
import SignalKnowledgeBase from '../pages/SignalKnowledgeBase';
import PayoutDetails from '../pages/finance/PayoutDetails';
import FinanceHome from '../pages/finance/FinanceHome';
import PayoutsAdmin from '../pages/admin/PayoutsAdmin';
import AdminVideoCall from '../pages/admin/AdminVideoCall';
import AdminTrialCampaign from '../pages/admin/AdminTrialCampaign';
import AdminEnquiries from '../pages/admin/AdminEnquiries';
import AdminSmsTemplates from '../pages/admin/AdminSmsTemplates';
import AdminWhatsAppTemplates from '../pages/admin/AdminWhatsAppTemplates';
import AdminFeatureRequests from '../pages/admin/AdminFeatureRequests';
import RefreshedDesignShowcase from '../pages/mockups/RefreshedDesignShowcase';

/**
 * AppRoutes component - Must be inside Router context
 * Handles all routing and keyboard shortcuts
 */
// Wraps `element` in <ProtectedRoute> using the roles/adminOrgOnly policy
// declared for `path` in config/navMenuItems.js (the same array Sidebar.js
// uses to decide link visibility) - a path with no declared policy there
// is returned unwrapped/unrestricted, so this only ever narrows the small
// set of nav-linked pages that already had one.
const protect = (path, element) => {
    const policy = getMenuItemAccess(path);
    if (!policy) return element;
    return <ProtectedRoute roles={policy.roles} adminOrgOnly={policy.adminOrgOnly} hideForAdminOrg={policy.hideForAdminOrg}>{element}</ProtectedRoute>;
};

export const AppRoutes = ({ user, onLogin }) => {
    const [showShortcuts, setShowShortcuts] = useState(false);

    // Enable global keyboard shortcuts (now inside Router context)
    useKeyboardShortcuts({
        onShowShortcuts: () => setShowShortcuts(true)
    });

    return (
        <>
            <GTMTracker user={user} />
            <Routes>
                <Route path="/" element={<Login onLogin={onLogin} />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/register" element={<Register />} />
                <Route path="/user" element={protect("/user", <User />)} />
                <Route path="/user-edit" element={<UserEdit />} />
                <Route path="/chat" element={<ChatSession user={user} />} />
                <Route path="/progress" element={<Progress user={user} />} />
                <Route path="/ConsultationHistory" element={protect("/ConsultationHistory", <ConsultationHistory />)} />
                <Route path="/dashboard" element={protect("/dashboard", <Dashboard />)} />
                <Route path="/getting-started" element={protect("/getting-started", <GettingStarted />)} />
                <Route path="/help" element={protect("/help", <Help />)} />
                <Route path="/help/requests" element={protect("/help/requests", <TeamRequests />)} />
                <Route path="/admin/enquiries" element={protect("/admin/enquiries", <AdminEnquiries />)} />
                <Route path="/admin/sms-templates" element={protect("/admin/sms-templates", <AdminSmsTemplates />)} />
                <Route path="/admin/whatsapp-templates" element={protect("/admin/whatsapp-templates", <AdminWhatsAppTemplates />)} />
                <Route path="/admin/feature-requests" element={protect("/admin/feature-requests", <AdminFeatureRequests />)} />
                <Route path="/therapist-dashboard" element={<TherapistDashboard />} />
                <Route path="/dashboard/benchmark/:id" element={<TherapistDashboard />} />
                <Route path="/appointment" element={protect("/appointment", <Appointment />)} />
                <Route path="/today" element={protect("/today", <TodayQueue />)} />
                <Route path="/risk-register" element={protect("/risk-register", <RiskRegister />)} />
                <Route path="/toolkit" element={protect("/toolkit", <Toolkit />)} />
                <Route path="/my-team" element={<Navigate to="/supervisor/dashboard" replace />} />
                <Route path="/supervision" element={protect("/supervision", <Supervision />)} />
                <Route path="/oversight" element={protect("/oversight", <Oversight />)} />
                <Route path="/audit-log" element={protect("/audit-log", <AuditLog />)} />
                <Route path="/admin/translations/questionnaires" element={protect("/admin/translations/questionnaires", <AdminTranslations kind="questionnaires" />)} />
                <Route path="/admin/translations/toolkit" element={protect("/admin/translations/toolkit", <AdminTranslations kind="toolkit" />)} />
                <Route path="/admin/questionnaires" element={protect("/admin/questionnaires", <AdminQuestionnaires />)} />
                <Route path="/patient" element={protect("/patient", <Patient />)} />
                <Route path="/patient/:id" element={protect("/patient", <PatientRecord />)} />
                <Route path="/patient-new" element={<PatientNew />} />
                <Route path="/patient-edit" element={<PatientEdit />} />
                <Route path="/appointment-new" element={<AppointmentNew />} />
                <Route path="/appointment-edit" element={<AppointmentEdit />} />
                <Route path="/consultation-new" element={<ConsultationNew />} />
                <Route path="/consultation-new/:id" element={<ConsultationNew />} />
                <Route path="/consultation/:id" element={<ConsultationEdit />} />
                <Route path="/consultation/:id/report-editor" element={<ConsultationReportEditor />} />
                <Route path="/doctor" element={protect("/doctor", <Doctor />)} />
                <Route path="/doctor-new" element={<DoctorNew />} />
                <Route path="/doctor-edit" element={<DoctorEdit />} />
                <Route path="/avatar" element={<Avatar />} />
                <Route path="/avatar-new" element={<AvatarNew />} />
                <Route path="/avatar-edit/:id" element={<AvatarEdit />} />
                <Route path="/student" element={<Student />} />
                <Route path="/student-new" element={<StudentNew />} />
                <Route path="/student-edit" element={<StudentEdit />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/subscription-locked" element={<SubscriptionLocked />} />
                <Route path="/calendar" element={protect("/calendar", <CalendarPage />)} />
                <Route path="/doctor-settings" element={<DoctorSettings />} />
                <Route path="/chistory" element={<CreditHistory />} />
                <Route path="/test-connections" element={<ConnectionTest />} />
                <Route path="/rag-debugger" element={<RagDebugger />} />
                <Route path="/admin/upload-session" element={<UploadSession />} />
                <Route path="/admin/manual-review" element={<ManualReviewDashboard />} />
                <Route path="/admin/review-settings" element={<ReviewSettings />} />
                <Route path="/admin/error-logs" element={protect("/admin/error-logs", <ErrorLogs />)} />
                <Route path="/admin/organization-credits" element={protect("/admin/organization-credits", <OrganizationCredits />)} />
                <Route path="/admin/organizations" element={protect("/admin/organizations", <AdminOrganizations />)} />
                <Route path="/admin/video-call" element={protect("/admin/video-call", <AdminVideoCall />)} />
                <Route path="/admin/trial-campaign" element={protect("/admin/trial-campaign", <AdminTrialCampaign />)} />
                <Route path="/admin/consultations-overview" element={protect("/admin/consultations-overview", <AdminConsultations />)} />
                <Route path="/admin/signal-knowledge" element={protect("/admin/signal-knowledge", <SignalKnowledgeBase />)} />
                <Route path="/finance" element={protect("/finance", <FinanceHome />)} />
                <Route path="/finance/payout-details" element={<PayoutDetails />} />
                <Route path="/admin/payouts" element={protect("/admin/payouts", <PayoutsAdmin />)} />
                <Route path="/new-session" element={<ConsultationNew />} />
                <Route path="/new-session" element={<ConsultationNew />} />
                <Route path="/sessions" element={<ConsultationHistory />} />
                <Route path="/analytics/competency" element={<LongitudinalReport />} />
                <Route path="/admin/progress-report" element={<ProgressReport />} />
                <Route path="/achievements" element={<Achievements />} />
                <Route path="/supervisor/dashboard" element={protect("/supervisor/dashboard", <SupervisorDashboard />)} />
                <Route path="/admin/manage-supervisors" element={protect("/admin/manage-supervisors", <ManageSupervisors />)} />
                <Route path="/supervisor/reportees" element={protect("/supervisor/reportees", <SupervisorReportees />)} />
                <Route path="/supervisor/reportees/:reporteeId/sessions" element={<ReporteeSessions />} />
                <Route path="/terms" element={<TermsConditions />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/mockups" element={<RefreshedDesignShowcase />} />
                <Route path="/design-preview" element={<RefreshedDesignShowcase />} />
            </Routes>
            <KeyboardShortcutsModal
                isOpen={showShortcuts}
                onClose={() => setShowShortcuts(false)}
            />
        </>
    );
};
